class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
    this.iigIntentHandler = (event) => {
      if (event.type === 'keydown' && !['Space', 'Enter'].includes(event.code)) return;
      if (event.target instanceof Element && event.target.closest('#search-icon-open, #account-icon-open')) this.iigIntentRevision = (this.iigIntentRevision || 0) + 1;
    };
  }

  connectedCallback() {
    document.addEventListener('click', this.iigIntentHandler, true);
    document.addEventListener('keydown', this.iigIntentHandler, true);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.iigIntentHandler, true);
    document.removeEventListener('keydown', this.iigIntentHandler, true);
  }

  getRenderContext() {
    return { closeRevision: this.iigCloseRevision || 0, intentRevision: this.iigIntentRevision || 0 };
  }

  isRenderContextCurrent(context) {
    if (context?.allowOpen === false) return false;
    const current = this.getRenderContext();
    return !context || (context.closeRevision === current.closeRevision && context.intentRevision === current.intentRevision && !document.querySelector('search-modal.active, account-modal.active'));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");

    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const opening = this.iigOpening = {};
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.hydrateDeferredImages();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      if (this.iigOpening !== opening) return;
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        if (this.iigOpening !== opening || !this.classList.contains("active")) return;
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("cart-overflow-hidden");

    if (!isSafari()) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.add("overflow-clip");
    }
  }

  hydrateDeferredImages() {
    this.querySelectorAll("[data-iig-deferred-drawer-image]").forEach((image) => {
      if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      if (image.dataset.src) image.src = image.dataset.src;
      image.removeAttribute("data-srcset");
      image.removeAttribute("data-src");
      image.removeAttribute("data-iig-deferred-drawer-image");
    });
  }

  close() {
    this.iigCloseRevision = (this.iigCloseRevision || 0) + 1;
    this.iigOpening = null;
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("cart-overflow-hidden");

    if (!isSafari()) {
      document.body.classList.remove("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-clip");
    }

    setTimeout(() => {
      let notification = this.querySelector(".notification");

      if (notification) {
        const activeItem = this.querySelector(
          `.cart-item[data-product-id="${this.productId}"]`
        );
        notification.classList.remove("notification-open");
        activeItem?.classList.remove("active");
      }
    }, 1000);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState, renderContext) {
    const context = renderContext || this.getRenderContext();
    const sections = this.getSectionsToRender();
    const valid = parsedState?.sections && sections.every((section) => {
      const html = parsedState.sections[section.id];
      return typeof html === 'string' && html.trim() && new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section');
    });
    if (!valid) {
      if (this.isRenderContextCurrent(context)) window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      return;
    }
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    let notification = this.querySelector(".notification");

    if (notification) {
      const activeItem = this.querySelector(
        `.cart-item[data-product-id="${this.productId}"]`
      );
      notification.classList.add("notification-open");
      activeItem?.classList.add("active");
    }

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      if (this.isRenderContextCurrent(context)) this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
